import { RefreshCcw, Search, SlidersHorizontal, Tag } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Badge, Section, WorkspaceSkeleton } from '../components/UI'
import { ActionToast } from '../components/ActionToast'
import { useTeamScope } from '../components/TeamScope'
import {
  formatDuration,
  loadTodayOperations,
  shortThreadId,
  type DailyOperatingPressure,
  type QueuePressure,
  type WorkRecord
} from '../lib/liveOperations'
import { supabase } from '../lib/supabase'

type FilterMode = 'all' | 'waiting' | 'replied'

type WorkCategory = {
  id: string
  name: string
  team_id: string | null
}

type Complexity = 'simple' | 'standard' | 'complex' | 'high_complexity'

function benchmarkTone(value: boolean | null) {
  if (value === true) return 'good' as const
  if (value === false) return 'bad' as const
  return 'neutral' as const
}

function benchmarkLabel(value: boolean | null) {
  if (value === true) return 'Target met'
  if (value === false) return 'Target missed'
  return 'Not evaluated'
}

function complexityLabel(value: Complexity | null) {
  if (!value) return 'Unclassified'
  return value.replace(/_/g, ' ')
}

export function Queue() {
  const { selectedTeamId } = useTeamScope()
  const [records, setRecords] = useState<WorkRecord[]>([])
  const [queuePressure, setQueuePressure] = useState<QueuePressure | null>(null)
  const [operatingPressure, setOperatingPressure] = useState<DailyOperatingPressure | null>(null)
  const [oldestOperatingWaitSeconds, setOldestOperatingWaitSeconds] = useState<number | null>(null)
  const [categories, setCategories] = useState<WorkCategory[]>([])
  const [mode, setMode] = useState<FilterMode>('all')
  const [search, setSearch] = useState('')
  const [editingThread, setEditingThread] = useState<string | null>(null)
  const [classificationCategory, setClassificationCategory] = useState('')
  const [classificationComplexity, setClassificationComplexity] = useState<Complexity>('standard')
  const [savingClassification, setSavingClassification] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function refresh() {
    setLoading(true)
    setError('')

    try {
      const data = await loadTodayOperations(selectedTeamId)
      setRecords(data.records)
      setQueuePressure(data.queuePressure)
      setOperatingPressure(data.operatingPressure)

      if (supabase) {
        const { data: categoryRows, error: categoryError } = await supabase
          .from('work_categories')
          .select('id,name,team_id')
          .eq('organisation_id', data.organisationId)
          .eq('is_active', true)
          .order('name', { ascending: true })

        if (categoryError) throw categoryError
        setCategories(((categoryRows ?? []) as WorkCategory[]).filter(category => !selectedTeamId || category.team_id == null || category.team_id === selectedTeamId))

        const startTime = String(data.team?.start_time ?? '08:00')
        const [startHour, startMinute] = startTime.split(':').map(value => Number(value))
        const operatingStart = new Date()
        operatingStart.setHours(
          Number.isFinite(startHour) ? startHour : 8,
          Number.isFinite(startMinute) ? startMinute : 0,
          0,
          0
        )

        if (selectedTeamId && data.mailboxIds.length === 0) {
          setOldestOperatingWaitSeconds(null)
        } else {
          let oldestWaitingQuery = supabase
            .from('thread_queue_state')
            .select('current_wait_seconds,last_inbound_at,mailbox_id')
            .eq('organisation_id', data.organisationId)
            .eq('is_waiting', true)
            .gte('last_inbound_at', operatingStart.toISOString())

          if (selectedTeamId) oldestWaitingQuery = oldestWaitingQuery.in('mailbox_id', data.mailboxIds)

          const { data: oldestWaiting, error: oldestWaitingError } = await oldestWaitingQuery
            .order('current_wait_seconds', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (oldestWaitingError) {
            console.warn('[Mail Percent] Could not load operating-window wait', oldestWaitingError)
            setOldestOperatingWaitSeconds(null)
          } else {
            setOldestOperatingWaitSeconds(
              oldestWaiting?.current_wait_seconds == null
                ? null
                : Number(oldestWaiting.current_wait_seconds)
            )
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load live work.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [selectedTeamId])

  const filtered = useMemo(() => {
    return records.filter(record => {
      const modeMatch = mode === 'all'
        || (mode === 'waiting' && record.work_status !== 'replied')
        || (mode === 'replied' && record.work_status === 'replied')
      const q = search.trim().toLowerCase()
      const searchMatch = !q || record.external_thread_id.toLowerCase().includes(q) || (record.subject ?? '').toLowerCase().includes(q)
      return modeMatch && searchMatch
    })
  }, [records, mode, search])

  const waiting = records.filter(record => record.work_status !== 'replied').length
  const replied = records.filter(record => record.work_status === 'replied').length

  function beginClassification(record: WorkRecord) {
    setEditingThread(record.external_thread_id)
    setClassificationCategory(record.category_id ?? '')
    setClassificationComplexity(record.complexity ?? 'standard')
    setMessage('')
    setError('')
  }

  async function saveClassification(record: WorkRecord) {
    if (!supabase) return

    if (!record.mailbox_id) {
      setError('This customer email cannot be classified until it is attached to a connected mailbox.')
      return
    }

    if (!classificationCategory) {
      setError('Choose a work category first.')
      return
    }

    setSavingClassification(true)
    setError('')
    setMessage('')

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser()
      if (authError || !authData.user) throw new Error('You are not signed in.')

      const { error: upsertError } = await supabase
        .from('thread_classifications')
        .upsert(
          {
            organisation_id: record.organisation_id,
            mailbox_id: record.mailbox_id,
            provider: record.provider,
            provider_thread_id: record.external_thread_id,
            category_id: classificationCategory,
            complexity: classificationComplexity,
            classification_source: 'manual',
            confidence: null,
            classified_by: authData.user.id,
            classified_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            onConflict: 'organisation_id,mailbox_id,provider,provider_thread_id'
          }
        )

      if (upsertError) throw upsertError

      setEditingThread(null)
      setMessage('Customer email classification saved. Mail Percent has reapplied the most specific matching SLA target.')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save classification.')
    } finally {
      setSavingClassification(false)
    }
  }

  if (loading) return <WorkspaceSkeleton rows={3}/>

  return <div className="stack-lg">
    {error && <div className="notice notice-inline"><strong>Live data unavailable:</strong><span>{error}</span></div>}
    {message && <ActionToast title="Classification updated" message={message} onDismiss={() => setMessage('')}/>}

    <div className="page-actions">
      <div className="segmented">
        <button className={mode === 'all' ? 'active' : ''} onClick={() => setMode('all')}>All <b>{records.length}</b></button>
        <button className={mode === 'waiting' ? 'active' : ''} onClick={() => setMode('waiting')}>Waiting <b>{waiting}</b></button>
        <button className={mode === 'replied' ? 'active' : ''} onClick={() => setMode('replied')}>Replied <b>{replied}</b></button>
      </div>

      <button className="button ghost" onClick={refresh} disabled={loading}>
        <RefreshCcw size={16}/>
        {loading ? 'Refreshing…' : 'Refresh'}
      </button>
    </div>

    <div className="queue-layout queue-layout-clean queue-layout-stretch">
      <Section
        title="Live measured work"
        subtitle="Response timing, classification and SLA result from provider mailbox data"
        className="queue-main"
      >
        <div className="queue-toolbar">
          <div className="search inline">
            <Search size={16}/>
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search customer email"/>
          </div>

          <button className="icon-btn" aria-label="Clear filters" onClick={() => { setSearch(''); setMode('all') }}>
            <SlidersHorizontal size={17}/>
          </button>
        </div>

        <div className="queue-list">
          {filtered.map((record, index) => {
            const category = categories.find(item => item.id === record.category_id)

            return (
              <div className="queue-row" key={`${record.user_id}:${record.external_thread_id}`}>
                <div className="queue-rank">{index + 1}</div>

                <div className="queue-content">
                  <div>
                    <strong title={record.subject ?? undefined}>{record.subject || `Customer email ${shortThreadId(record.external_thread_id)}`}</strong>
                    <span>
                      {record.work_status === 'replied' && record.arrival_to_reply_sent_seconds != null
                        ? `${formatDuration(record.arrival_to_reply_sent_seconds)} received → first reply`
                        : record.inbound_received_at
                          ? `Received ${new Date(record.inbound_received_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                          : 'Received time unavailable'}
                    </span>
                  </div>

                  <div className="queue-tags">
                    <Badge tone={record.work_status === 'replied' ? 'good' : 'warn'}>
                      {record.work_status === 'replied' ? 'Replied' : 'Waiting'}
                    </Badge>

                    <Badge tone={benchmarkTone(record.reply_sent_target_met)}>
                      {benchmarkLabel(record.reply_sent_target_met)}
                    </Badge>

                    <Badge tone={record.category_id ? 'blue' : 'neutral'}>
                      {category?.name ?? 'Unclassified'}
                    </Badge>

                    <Badge tone="neutral">
                      {complexityLabel(record.complexity)}
                    </Badge>
                  </div>

                  {editingThread === record.external_thread_id && (
                    <div className="benchmark-editor">
                      <div className="field">
                        <label>Category</label>
                        <select
                          value={classificationCategory}
                          onChange={event => setClassificationCategory(event.target.value)}
                        >
                          <option value="">Choose category</option>
                          {categories.map(item => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="field">
                        <label>Complexity</label>
                        <select
                          value={classificationComplexity}
                          onChange={event => setClassificationComplexity(event.target.value as Complexity)}
                        >
                          <option value="simple">Simple</option>
                          <option value="standard">Standard</option>
                          <option value="complex">Complex</option>
                          <option value="high_complexity">High complexity</option>
                        </select>
                      </div>

                      <div className="page-actions">
                        <button
                          type="button"
                          className="button ghost"
                          onClick={() => setEditingThread(null)}
                        >
                          Cancel
                        </button>

                        <button
                          type="button"
                          className="button primary"
                          disabled={savingClassification}
                          onClick={() => saveClassification(record)}
                        >
                          {savingClassification ? 'Saving…' : 'Save classification'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="queue-meta">
                  <strong>
                    {record.reply_sent_target_seconds != null
                      ? `Response SLA ≤ ${formatDuration(record.reply_sent_target_seconds)}`
                      : 'No response SLA'}
                  </strong>

                  <span>
                    {record.arrival_to_reply_sent_seconds != null
                      ? `${formatDuration(record.arrival_to_reply_sent_seconds)} received → reply sent`
                      : 'Waiting for first reply'}
                  </span>

                  {record.mailbox_type === 'shared' && record.work_status === 'replied' && (
                    <div className="handler-attribution">
                      {record.handled_by_user_id ? (
                        <><span>Handled by</span><strong>{record.handled_by_name || record.handled_by_email || 'Team member'}</strong></>
                      ) : record.handled_by_email ? (
                        <><span>Provider sender</span><strong>{record.handled_by_email}</strong><small>Not matched to an assigned employee</small></>
                      ) : (
                        <small className="handler-unknown">Handler not identifiable from provider metadata</small>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="button ghost"
                    onClick={() => beginClassification(record)}
                  >
                    <Tag size={15}/>
                    Classify
                  </button>
                </div>
              </div>
            )
          })}

          {!loading && filtered.length === 0 && <p className="muted">No measured customer emails match this view.</p>}
        </div>
      </Section>

      <Section title="Customer email pressure" subtitle="What is waiting and what your team is dealing with today" className="queue-context-panel">
        <div className="context-grid queue-pressure-six">
          <div><span>Waiting now</span><strong>{queuePressure?.waiting_threads ?? 0}</strong></div>
          <div><span>Waiting over 15 min</span><strong>{queuePressure?.waiting_over_15m ?? 0}</strong></div>
          <div><span>Waiting over 30 min</span><strong>{queuePressure?.waiting_over_30m ?? 0}</strong></div>
          <div><span>Longest wait today</span><strong>{formatDuration(oldestOperatingWaitSeconds)}</strong></div>
          <div><span>Customer emails received today</span><strong>{operatingPressure?.inbound_threads ?? 0}</strong></div>
          <div><span>Employees active today</span><strong>{operatingPressure?.active_people ?? 0}</strong></div>
        </div>

        <p className="muted queue-context-note">
          “Longest wait today” only considers customer emails received during today’s operating window.
        </p>
      </Section>
    </div>
  </div>
}
